// Why an offer is, or is not, in front of the trader — for admin screens only.
//
// ⚠️ THIS MIRRORS SQL, AND THE SQL IS THE AUTHORITY.
// get_request_pending_review_count (migration 20260919120440) counts a held
// offer as:
//
//   managed_visibility = 'admin_only'
//   and visibility_reason = 'unverified_supplier'
//   and lower(coalesce(status, '')) <> 'cancelled'
//
// isHeldForVerification below is that predicate in JS, and the two must move
// together. A board that counts a different set from the screen it links to is
// the isReviewable()/fetchReadyCount() bug in src/lib/consoleVerification.js,
// and it presents as a data problem rather than as a code one.
//
// ── The two admin_only reasons are NOT interchangeable ─────────────────────
// They are opposite situations that happen to share a column value:
//
//   unverified_supplier — a real quote from a real supplier, held because
//     nobody has approved them yet. It is WAITING ON US. Approving the supplier
//     releases this exact offer to the trader (20260918184502).
//
//   managed_raw — a raw supplier price on a managed order. The trader will
//     NEVER see it, by design: on a managed order we negotiate and present one
//     coordinated price. Nothing is pending and there is nothing to approve.
//
// Rendering them alike would tell staff that a managed raw quote is stuck
// waiting for them. That is why the count function excludes managed_raw
// explicitly, and why the chip below carries no action for it.
//
// This file makes NO decision about what a trader sees. That is offers_select
// (migration 20260918172830) and nothing else — golden rule 2's point that the
// gate lives in RLS, never client-side.

const vis = (o) => String(o?.managed_visibility ?? '');
const reason = (o) => String(o?.visibility_reason ?? '');
const cancelled = (o) => String(o?.status ?? '').toLowerCase() === 'cancelled';
const lc = (v) => String(v ?? '').toLowerCase();

/**
 * The trader is shown ONE curated price for this request, not suppliers' raw
 * quotes.
 *
 * ⚠️ MIRRORS public.request_is_curated(uuid), which both server-side readers
 * call — get_request_pending_review_count and
 * reveal_quotes_on_supplier_approval. Same classification the board already
 * derives its `kind` from (src/lib/consoleQuotations.js:110-113).
 *
 * idea_to_product counts as curated: the same concierge service started from
 * an idea. Being wrong this way hides a chip; being wrong the other way tells
 * an admin that approving a supplier will release a quote when it will not.
 */
export function isCuratedRequest(request) {
  return ['managed', 'idea_to_product'].includes(lc(request?.request_kind))
    || lc(request?.sourcing_mode) === 'managed';
}

/**
 * Held from the trader only until someone approves the supplier. Actionable.
 *
 * `request` is REQUIRED for the actionable answer and the reason is the whole
 * shape of this fix: since the guard records the CAUSE rather than the caller,
 * an offer on a curated request can carry 'unverified_supplier' and still
 * never be released — reveal_quotes_on_supplier_approval excludes curated
 * requests. Treating it as actionable would put a "Verify supplier" button in
 * front of an admin whose press changes nothing the trader can see.
 *
 * Called without a request it answers the narrow question "is this row
 * labelled held", which is what the raw predicate means and all a caller
 * holding no request can honestly ask.
 */
export function isHeldForVerification(offer, request = null) {
  const labelled = vis(offer) === 'admin_only'
    && reason(offer) === 'unverified_supplier'
    && !cancelled(offer);
  if (!labelled) return false;
  return request ? !isCuratedRequest(request) : true;
}

/** A raw managed quote the trader is never shown. Not actionable, not a problem. */
export function isManagedRaw(offer) {
  return vis(offer) === 'admin_only' && reason(offer) === 'managed_raw';
}

/**
 * How many offers on this request are waiting on a verification decision.
 *
 * Mirrors get_request_pending_review_count, which returns 0 outright for a
 * curated request — so this does too, rather than counting rows the server
 * would not.
 */
export function heldForVerificationCount(offers, request = null) {
  if (request && isCuratedRequest(request)) return 0;
  return (offers || []).filter((o) => isHeldForVerification(o, request)).length;
}

// Trilingual because the app's language is trilingual, even though the console
// itself currently offers ar/en: lang can still arrive as 'zh', and a three-way
// lookup means that string is used rather than sitting here as decoration.
const LABEL = {
  held: {
    ar: 'محجوز — المورّد غير موثّق',
    en: 'held — supplier unverified',
    zh: '暂不展示 — 供应商未认证',
  },
  managedRaw: {
    ar: 'عرض مُدار — لا يُعرض للتاجر',
    en: 'managed quote — not shown to trader',
    zh: '托管报价 — 不向买家展示',
  },
};

const pick = (entry, lang) => entry[lang] || entry.en;

/**
 * The chip for one offer, or null when there is nothing to say.
 *
 * `action: true` means the chip should carry a link to the supplier, because
 * approving them is what clears it. managed_raw gets no link on purpose: there
 * is no decision to go and make, and a link would imply one.
 *
 * A buyer_visible offer returns null rather than a "visible" chip — the normal
 * case should be quiet, or the exceptions stop standing out.
 */
export function offerVisibilityChip(offer, request = null, lang = 'en') {
  if (vis(offer) !== 'admin_only') return null;

  // Curated request first, whatever the reason says. After the 2026-09-19
  // precedence fix an unverified supplier's raw quote on a managed order
  // carries 'unverified_supplier' — truthfully, that IS why it is hidden — but
  // approving them releases nothing, because the releaser excludes curated
  // requests. The chip has to describe what will actually happen, not what the
  // column says in isolation.
  if (request && isCuratedRequest(request)) {
    return { label: pick(LABEL.managedRaw, lang), tone: 'neutral', action: false };
  }
  if (isHeldForVerification(offer, request)) {
    return { label: pick(LABEL.held, lang), tone: 'warn', action: true };
  }
  if (isManagedRaw(offer)) {
    return { label: pick(LABEL.managedRaw, lang), tone: 'neutral', action: false };
  }
  return null;
}

/** The board's chip: "N held", or null when none are. */
export function heldChipLabel(count, lang = 'en') {
  if (!count) return null;
  if (lang === 'ar') return `${count} محجوز`;
  if (lang === 'zh') return `${count} 项暂不展示`;
  return `${count} held`;
}
